import React from "react";
import {
  ArrowRight,
  Buildings,
  ChartBar,
  CurrencyCircleDollar,
  Handshake,
  PhoneCall,
  Storefront,
  Truck,
  UsersThree,
} from "@phosphor-icons/react";
import RghtnowMarketingHeader from "./RghtnowMarketingHeader";
import RghtnowMarketingFooter from "./RghtnowMarketingFooter";
import { Link } from "react-router-dom";
import AnimatedStats from "../components/AnimatedStats";
import "./rghtnow-pages.css";

const upload = (path) => `https://rghtnow.com/wp-content/uploads/${path}`;

const heroHighlights = [
  {
    title: "Wide Reach",
    text: "Access to millions of customers",
    icon: UsersThree,
    tone: "orange",
  },
  {
    title: "Multiple Categories",
    text: "Supply grocery, food, pharmacy & more.",
    icon: Buildings,
    tone: "green",
  },
  {
    title: "Timely Payments",
    text: "Secure and on-time payments always",
    icon: CurrencyCircleDollar,
    tone: "brown",
  },
  {
    title: "Trusted Platform",
    text: "Partner with a growing marketplace",
    icon: Handshake,
    tone: "yellow",
  },
];

const vendorRows = [
  ["Grocery Stores & Supermarkets", "500+"],
  ["Restaurants & Food Outlets", "300+"],
  ["Pharmacies & Medical Stores", "150+"],
  ["Flower & Gift Shops", "100+"],
  ["Electronics & Mobile Stores", "120+"],
  ["Fashion & Uniform Suppliers", "80+"],
  ["Handyman & Local Service Providers", "200+"],
  ["Local Retail & Specialty Stores", "250+"],
];

const deliveryRows = [
  ["Bike Delivery Executives", "400+"],
  ["Taxi & Auto Partners", "150+"],
  ["Hyperlocal Delivery Riders", "250+"],
  ["Freelance Delivery Associates", "100+"],
];

const benefits = [
  {
    image: upload("2026/05/Partnerships.svg"),
    title: "Expand Your Customer Reach",
    text: "Connect with a larger customer base through our digital marketplace platform.",
  },
  {
    image: upload("2026/05/Increase-Sales.svg"),
    title: "Increase Sales Opportunities",
    text: "Receive online orders and service requests from customers across multiple segments.",
  },
  {
    image: upload("2026/06/Group-9165.svg"),
    title: "Easy Digital Integration",
    text: "Simple onboarding and user-friendly systems to manage products, orders, and services.",
  },
  {
    image: upload("2026/05/Advertising.svg"),
    title: "Marketing & Promotional Support",
    text: "Get visibility through platform promotions, campaigns, and digital marketing activities.",
  },
  {
    image: upload("2026/05/cities.svg"),
    title: "Business Growth Support",
    text: "We help local businesses grow by providing modern eCommerce and service solutions.",
  },
];

const steps = [
  {
    image: upload("2026/05/Register.svg"),
    title: "1. Register",
    text: "Submit your business details and documents",
  },
  {
    image: upload("2026/05/Get-Varified.svg"),
    title: "2. Get Verified",
    text: "Our team will verify your information and products.",
  },
  {
    image: upload("2026/05/List-your-prodcuts.svg"),
    title: "3. List Your Products",
    text: "Add your products with pricing and availability.",
  },
  {
    image: upload("2026/05/Starting-Supplying.svg"),
    title: "4. Start Supplying",
    text: "Receive purchase orders and start supplying.",
  },
  {
    image: upload("2026/05/Increase-Sales.svg"),
    title: "5. Grow With Us",
    text: "Build long-term partnership and scale",
  },
];

const partnerSegments = [
  { image: upload("2026/05/grocery-img-300x300.png"), title: "Grocery Stores & Supermarkets" },
  { image: upload("2026/05/food-300x300.png"), title: "Restaurants & Food Outlets" },
  { image: upload("2026/05/electronic-300x300.png"), title: "Electronics Stores" },
  { image: upload("2026/05/taxi-300x300.png"), title: "Taxi & Transportation Providers" },
  { image: upload("2026/05/pharmacy-300x300.png"), title: "Pharmacies & Healthcare Stores" },
  { image: upload("2026/05/Handy-Man-300x300.png"), title: "Handyman & Home Service Providers" },
  { image: upload("2026/06/mobile-recharge-300x300.png"), title: "Mobile Recharge & Utility Service Providers" },
  { image: upload("2026/05/Local-Shop-300x300.png"), title: "Local Retail Stores" },
  { image: upload("2026/05/Uniform-300x300.png"), title: "Uniform Manufacturers & Suppliers" },
  { image: upload("2026/05/electronic-300x300.png"), title: "Delivery & Logistics Partners" },
];

const VendorsDeliveryPartnersPage = () => (
  <div className="rght-page vendors-delivery-partners-page">
    <RghtnowMarketingHeader active="vendors" />

    <main>
      <section className="rght-hero">
        <div className="container rght-hero-grid">
          <div className="rght-hero-copy">
            <span className="rght-kicker">Vendors & Delivery Partners</span>
            <h1>
              Grow Your Business. Earn on Your <em>Own Terms</em>
            </h1>
            <p>
              At RGHT NOW, our trusted vendors and delivery partners are the backbone of our fast and reliable
              service network. We collaborate with local businesses, suppliers, and service providers to ensure
              customers receive quality products and timely deliveries.
            </p>
            <div className="rght-actions">
              <Link className="rght-button rght-button-pink" to="/onboard-your-store">
                Onboard Your Store <ArrowRight size={18} />
              </Link>
              <a className="rght-button rght-button-line" href="https://rghtnow.com/contact-us/" target="_blank" rel="noreferrer">
                Talk to Our Team <PhoneCall size={17} weight="fill" />
              </a>
            </div>
          </div>
          <div className="rght-hero-art">
            <div className="rght-hero-shape" />
            <img src={upload("2026/06/Home-1024x741.jpeg")} alt="RGHT NOW vendors and delivery network" />
          </div>
        </div>
      </section>

      <section className="rght-mini-band">
        <div className="container rght-mini-grid">
          {heroHighlights.map((item) => {
            const Icon = item.icon;

            return (
              <article className={`rght-mini-card ${item.tone}`} key={item.title}>
                <span>
                  <Icon size={34} weight="fill" />
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="rght-section">
        <div className="container">
          <div className="rght-heading">
            <h2>
              Partner Network <em>Overview</em>
            </h2>
            <p>We're building a comprehensive ecosystem of vendors and delivery partners across multiple categories to serve customers better.</p>
          </div>
          <div className="rght-overview-grid">
            <article className="rght-table-card">
              <div className="rght-table-title">
                <div>
                  <h3>Targeted Vendor Segments</h3>
                  <span>Vendors</span>
                </div>
                <Storefront size={34} weight="fill" />
              </div>
              <div className="rght-table-row rght-table-head">
                <span>Segment</span>
                <span>Target</span>
              </div>
              {vendorRows.map(([segment, target]) => (
                <div className="rght-table-row" key={segment}>
                  <span>{segment}</span>
                  <strong>{target}</strong>
                </div>
              ))}
            </article>

            <article className="rght-table-card">
              <div className="rght-table-title">
                <div>
                  <h3>Delivery Partner Network</h3>
                  <span>Delivery</span>
                </div>
                <Truck size={34} weight="fill" />
              </div>
              <div className="rght-table-row rght-table-head">
                <span>Delivery Category</span>
                <span>Target</span>
              </div>
              {deliveryRows.map(([segment, target]) => (
                <div className="rght-table-row" key={segment}>
                  <span>{segment}</span>
                  <strong>{target}</strong>
                </div>
              ))}
            </article>
          </div>
        </div>
      </section>

      <section className="rght-section rght-soft">
        <div className="container">
          <div className="rght-heading">
            <h2>
              Benefits of Partnering with <em>RGHT NOW</em>
            </h2>
            <p>Expand your reach, increase earnings, and grow faster with the power of RGHT NOW.</p>
          </div>
          <div className="rght-card-grid rght-card-grid-five">
            {benefits.map((benefit) => (
              <article className="rght-icon-card" key={benefit.title}>
                <img src={benefit.image} alt="" />
                <h3>{benefit.title}</h3>
                <p>{benefit.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rght-section">
        <div className="container">
          <div className="rght-heading">
            <h2>
              How It <em>Works</em>
            </h2>
            <p>Five easy steps to start supplying and growing with RGHT NOW.</p>
          </div>
          <div className="rght-step-grid">
            {steps.map((step) => (
              <article className="rght-step-card" key={step.title}>
                <img src={step.image} alt="" />
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rght-section rght-soft">
        <div className="container">
          <div className="rght-heading">
            <h2>
              Who Can <em>Partner</em> With Us
            </h2>
            <p>We welcome vendors and suppliers from multiple business segments.</p>
          </div>
          <div className="rght-picture-grid">
            {partnerSegments.map((segment) => (
              <article className="rght-picture-card" key={segment.title}>
                <img src={segment.image} alt="" />
                <h3>{segment.title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <AnimatedStats subtitle="Our vendor and delivery partner network is built for fast expansion across high-demand local commerce categories." />

      <section className="rght-section rght-cta-section">
        <div className="container">
          <div className="rght-cta-panel">
            <img src={upload("2026/05/vendor-bottom.svg")} alt="" />
            <div>
              <span className="rght-kicker">Grow with RGHT NOW</span>
              <h2>Join Our Marketplace</h2>
              <p>Become a part of RGHT NOW and grow your business with a smart, connected, and rapidly expanding marketplace ecosystem.</p>
              <p>Together, we create better convenience for customers and better opportunities for businesses.</p>
            </div>
            <a className="rght-button rght-button-pink" href="#mobile-app-intro">
              Download Seller Guideline <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <section className="rght-seller-strip" id="mobile-app-intro">
        <div className="container rght-seller-inner">
          <div>
            <h2>Start Selling on RGHT NOW Today!</h2>
            <p>More orders. More customers. More growth.</p>
          </div>
          <div className="rght-store-badges">
            <img src={upload("2023/09/Google_Play_Store_badge_EN.svg-1024x303.png")} alt="Google Play" />
            <img src={upload("2023/09/Download_on_the_App_Store_Badge.svg-1024x304.webp")} alt="App Store" />
          </div>
          <ChartBar size={50} weight="fill" />
        </div>
      </section>
    </main>

    <RghtnowMarketingFooter />
  </div>
);

export default VendorsDeliveryPartnersPage;
